// MapBox Geocoding Service for address autocomplete and coordinate lookup
const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
const MAPBOX_BASE_URL = 'https://api.mapbox.com/geocoding/v5/mapbox.places';

class MapBoxGeocodingService {
  
  /**
   * Search for address suggestions based on user input
   * @param {string} query - The search query (address text)
   * @param {number} limit - Number of suggestions to return (default: 5)
   * @returns {Promise<Array>}
   */
  async searchAddresses(query, limit = 5) {
    if (!query || query.trim().length < 3) {
      return [];
    }

    // Check if API key is configured
    if (!MAPBOX_ACCESS_TOKEN) {
      console.error('❌ MapBox API key not configured. Please set REACT_APP_MAPBOX_ACCESS_TOKEN in your environment variables.');
      return [];
    }

    try {
      const encodedQuery = encodeURIComponent(query.trim());
      const url = `${MAPBOX_BASE_URL}/${encodedQuery}.json?access_token=${MAPBOX_ACCESS_TOKEN}&limit=${limit}&types=address,poi&autocomplete=true`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(`MapBox API error: ${data.message || response.statusText}`);
      }

      return (data.features || []).map((feature) => this.parseFeature(feature));
    } catch (error) {
      console.error('❌ Failed to search addresses:', error);
      return [];
    }
  }

  /**
   * Get coordinates for a specific address
   * @param {string} address - Full address string
   * @returns {Promise<Object|null>}
   */
  async geocodeAddress(address) {
    if (!address || address.trim().length === 0) {
      return null;
    }

    // Check if API key is configured
    if (!MAPBOX_ACCESS_TOKEN) {
      console.error('❌ MapBox API key not configured. Please set REACT_APP_MAPBOX_ACCESS_TOKEN in your environment variables.');
      return null;
    }

    try {
      const encodedAddress = encodeURIComponent(address.trim());
      const url = `${MAPBOX_BASE_URL}/${encodedAddress}.json?access_token=${MAPBOX_ACCESS_TOKEN}&limit=1&types=address`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(`MapBox API error: ${data.message || response.statusText}`);
      }

      const feature = data.features?.[0];
      if (!feature) {
        return null;
      }

      return this.parseGeocodeResult(feature);
    } catch (error) {
      console.error('❌ Failed to geocode address:', error);
      return null;
    }
  }

  /**
   * Reverse geocode coordinates to get address
   * @param {number} longitude - Longitude coordinate
   * @param {number} latitude - Latitude coordinate
   * @returns {Promise<Object|null>}
   */
  async reverseGeocode(longitude, latitude) {
    // Check if API key is configured
    if (!MAPBOX_ACCESS_TOKEN) {
      console.error('❌ MapBox API key not configured. Please set REACT_APP_MAPBOX_ACCESS_TOKEN in your environment variables.');
      return null;
    }

    try {
      const url = `${MAPBOX_BASE_URL}/${longitude},${latitude}.json?access_token=${MAPBOX_ACCESS_TOKEN}&types=address,poi,neighborhood,locality,place,postcode&limit=1`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(`MapBox API error: ${data.message || response.statusText}`);
      }

      const feature = data.features?.[0];
      if (!feature) {
        return null;
      }

      return this.parseGeocodeResult(feature);
    } catch (error) {
      console.error('❌ Failed to reverse geocode:', error);
      return null;
    }
  }

  /**
   * Parse MapBox feature into AddressSuggestion
   */
  parseFeature(feature) {
    const context = this.parseContext(feature.context || []);
    const street = feature.text;
    const houseNumber = feature.address || feature.properties?.address;
    const streetAddress = houseNumber ? `${houseNumber} ${street}` : street;
    const [longitude, latitude] = feature.center;
    
    return {
      id: feature.id,
      text: feature.text,
      placeName: feature.place_name,
      context,
      coordinates: feature.center, // [longitude, latitude]
      houseNumber,
      street,
      streetAddress,
      // Add address fields for autocomplete selection
      city: context.locality || undefined,
      state: context.region || undefined,
      zipCode: context.postcode || undefined,
      country: context.country || 'US',
      latitude,
      longitude,
      formattedAddress: feature.place_name,
    };
  }

  /**
   * Parse MapBox feature into GeocodeResult
   */
  parseGeocodeResult(feature) {
    const context = this.parseContext(feature.context || []);
    const [longitude, latitude] = feature.center;
    
    // Try to extract street address from the feature
    let streetAddress = feature.address ? `${feature.address} ${feature.text}` : feature.text;
    
    // If the feature text looks like a street number, use it with text
    if (feature.properties?.address) {
      streetAddress = `${feature.properties.address} ${feature.text}`;
    }

    return {
      streetAddress: streetAddress || undefined,
      city: context.locality || undefined,
      state: context.region || undefined,
      zipCode: context.postcode || undefined,
      country: context.country || 'US',
      latitude,
      longitude,
      formattedAddress: feature.place_name,
    };
  }

  /**
   * Parse MapBox context array into structured format
   */
  parseContext(contextArray) {
    const context = {};
    
    contextArray.forEach((item) => {
      const id = item.id || '';
      
      if (id.includes('locality')) {
        context.locality = item.text;
      } else if (id.includes('place')) {
        context.locality = context.locality || item.text; // Fallback to place if no locality
      } else if (id.includes('region')) {
        context.region = item.short_code ? item.short_code.replace('us-', '').toUpperCase() : item.text;
      } else if (id.includes('postcode')) {
        context.postcode = item.text;
      } else if (id.includes('country')) {
        context.country = item.short_code ? item.short_code.toUpperCase() : item.text;
      }
    });
    
    return context;
  }

  /**
   * Format coordinates for display
   */
  formatCoordinates(longitude, latitude) {
    return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
  }

  /**
   * Calculate distance between two coordinates (in kilometers)
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.deg2rad(lat2 - lat1);
    const dLon = this.deg2rad(lon2 - lon1);
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const d = R * c; // Distance in kilometers
    return d;
  }

  deg2rad(deg) {
    return deg * (Math.PI/180);
  }
}

const mapBoxGeocodingService = new MapBoxGeocodingService();
export default mapBoxGeocodingService;

